import { ReactNode, createContext, useEffect, useMemo, useState } from "react";

import { AxiosError } from "axios";
import { defaultsDeep } from "lodash-es";

import { ApiInterceptorError } from "@classes/ApiInterceptorError";
import {
  featureFlags,
  clientSettings as staticClientSettings,
} from "@settings";

import { useApiClientTheme } from "@hooks/api/theme";
import { mapClientThemeSettings } from "@mappers/client-theme";
import { isCardExclusiveEnv } from "@utils/cardExclusiveEnv";

export const ClientSettingsContext = createContext<ClientSettingsWithKeys>(
  null!,
);

const staticCardClientSettings = defaultsDeep(
  {},
  staticClientSettings?.cardExclusiveTheme ?? {},
  staticClientSettings,
) as ClientSettingsWithKeys;

export const ClientSettingsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [fetchTheme, { data, error, loading }] = useApiClientTheme();
  const [isStaticTheme, setIsStaticTheme] = useState(
    !featureFlags.enableCorrespondenteBanqueiro,
  );

  useEffect(() => {
    const fetch = async () => {
      if (isStaticTheme) return;
      try {
        await fetchTheme({
          data: { origin: location.origin },
        });
      } catch (e) {
        if (
          e instanceof ApiInterceptorError &&
          e.sourceError instanceof AxiosError
        ) {
          if (e.sourceError.response?.status === 404) {
            setIsStaticTheme(true);
          } else {
            console.error(e.sourceError.response);
            throw new Error("Ocorreu um erro ao recuperar o tema do cliente");
          }
        }
      }
    };

    void fetch();
  }, [fetchTheme, isStaticTheme]);

  const client = useMemo(() => {
    if (isStaticTheme) {
      if (isCardExclusiveEnv()) {
        return staticCardClientSettings;
      } else {
        return staticClientSettings;
      }
    }
    if (!data) return null;

    return mapClientThemeSettings(data);
  }, [data, isStaticTheme]);

  if (!isStaticTheme && error) return "Serviço temporariamente indisponível";
  if (loading || !client) return "";

  return (
    <ClientSettingsContext.Provider value={client}>
      {children}
    </ClientSettingsContext.Provider>
  );
};
